import { PoolDetails } from "@/models/pool-details.model";
import { Pool } from "@/models/pool.model";
import { getConstantValue } from "@/shared/utils/constants";
import { useApi } from "@/services/useApi.ts";

function chunk(str: string, size: number) {
  const numChunks = Math.ceil(str.length / size);
  const chunks = new Array(numChunks);

  for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
    chunks[i] = str.substr(o, size);
  }

  return chunks;
}

function deserialize(payload: string, numBits: number): number[] {
  return chunk(
    payload
      .split("")
      .map((a) =>
        a
          .charCodeAt(0)
          .toString(2)
          .padStart(8, "0")
          .split("")
          .reverse()
          .join("")
      )
      .join(""),
    numBits
  ).map((s) => parseInt(s.split("").reverse().join(""), 2));
}

function serialize(slots: number[], numBits: number): string {
  const binary = slots
    .map((s) =>
      s.toString(2).padStart(numBits, "0").split("").reverse().join("")
    )
    .join("");
  return chunk(binary, 8)
    .map((o) =>
      parseInt(o.split("").reverse().join(""), 2).toString(16).padStart(2, "0")
    )
    .join("");
}

/**
 * Adjust the filtration speed of the plan if needed
 * (PLAN_FILTREAUTO is encoded has binary but can have different speeds > 1 depending
 * on the VFSpeed1 params)
 * @param plan
 * @param poolDetails
 */
const adjustSpeed = (
  plan: Pool["plans"][number],
  poolDetails: PoolDetails
): Pool["plans"][number] => {
  if (plan.index === getConstantValue("PLAN_FILTREAUTO")) {
    const vOff = poolDetails.ExtraParams
      ? poolDetails.ExtraParams.VFSpeed1 & 0x07
      : 0;
    const vOn = poolDetails.ExtraParams
      ? (poolDetails.ExtraParams.VFSpeed1 >> 3) & 0x07
      : 1;
    return {
      ...plan,
      slots: plan.slots.map((speed) => (speed === 0 ? vOff : vOn)),
      forceLabels: vOff !== 0 || vOn !== 1,
    };
  }
  return plan;
};

export const deserializePlan = (
  { index, plan, plan64 }: PoolDetails["plans"][number],
  poolDetails: PoolDetails
): Pool["plans"][number] => {
  const payload = plan64 ? atob(plan64) : plan;

  return adjustSpeed(
    {
      index,
      slots: deserialize(payload, index === 254 ? 3 : 1),
      forceLabels: index === 254,
    },
    poolDetails
  );
};

export const useSavePlan = () => {
  const api = useApi();
  return (
    { index, slots }: Pool["plans"][number],
    poolID: number
  ): Promise<unknown> => {
    return api
      .setPlages({
        poolID,
        comMode: 1,
        planIdx: index,
        slots: serialize(slots, index > 200 ? 3 : 1),
      })
      .then(api.waitCommand);
  };
};
